import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow, format } from "date-fns";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Bell } from "lucide-react";
import {
  useOwnerNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from "@/hooks/useOwnerNotifications";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageContainer } from "@/components/layout/PageContainer";
import { cn } from "@/lib/utils";
import { RESTOCK_DELIVERED_TYPE } from "@/lib/restockDeliveredNotify";

type FilterType = "all" | "unread" | "cleaning_completed" | "inventory_low_stock" | "restock_delivered";

function notificationHref(n: {
  property_id: string;
  maintenance_issue_report_id?: string | null;
  maintenance_issue_id?: string | null;
  cleaning_task_id?: string | null;
  type: string;
}): string | null {
  if (n.maintenance_issue_report_id) {
    return `/properties/${n.property_id}?tab=maintenance&focusIssueId=${n.maintenance_issue_report_id}`;
  }
  if (n.maintenance_issue_id) {
    return `/properties/${n.property_id}?tab=maintenance&focusIssueId=${n.maintenance_issue_id}`;
  }
  if (n.type === RESTOCK_DELIVERED_TYPE) {
    return `/properties/${n.property_id}?tab=maintenance`;
  }
  if (n.cleaning_task_id) {
    return `/properties/${n.property_id}?tab=reports`;
  }
  return null;
}

export default function Notifications() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>("all");
  const { data: notifications = [], isLoading } = useOwnerNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const filteredNotifications = notifications.filter((n) => {
    if (filter === "unread") return !n.is_read;
    if (filter === "cleaning_completed") return n.type === "cleaning_completed";
    if (filter === "inventory_low_stock") return n.type === "inventory_low_stock";
    if (filter === "restock_delivered") return n.type === RESTOCK_DELIVERED_TYPE;
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const handleNotificationClick = (notification: (typeof notifications)[0]) => {
    if (!notification.is_read) {
      markRead.mutate(notification.id);
    }
    const href = notificationHref(notification);
    if (href) navigate(href);
  };

  return (
    <AppLayout>
      <Navbar />
      <PageContainer className="py-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/owner/properties")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Properties
        </Button>

        <Card className="overflow-hidden border-[#e6edf1]">
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <Bell className="h-5 w-5 shrink-0 text-[#475569]" />
                <CardTitle className="text-lg font-bold tracking-[-0.3px] text-[#0f1720] sm:text-xl">
                  Notifications
                </CardTitle>
                {unreadCount > 0 ? (
                  <span className="shrink-0 rounded-lg bg-[#e2eeed] px-2.5 py-1 text-[11px] font-bold text-[#15776d]">
                    {unreadCount} new
                  </span>
                ) : (
                  <span className="text-xs font-medium text-[#64748b]">All read</span>
                )}
              </div>
              <div className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:items-center">
                <Select
                  value={filter}
                  onValueChange={(v) => setFilter(v as FilterType)}
                >
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All notifications</SelectItem>
                    <SelectItem value="unread">Unread only</SelectItem>
                    <SelectItem value="restock_delivered">Delivered</SelectItem>
                    <SelectItem value="cleaning_completed">Completed cleanings</SelectItem>
                    <SelectItem value="inventory_low_stock">Low stock alerts</SelectItem>
                  </SelectContent>
                </Select>
                {unreadCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => markAllRead.mutate()}
                  >
                    Mark all read
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="px-6 py-8 text-center text-sm text-[#64748b]">Loading notifications...</div>
            ) : filteredNotifications.length === 0 ? (
              <div className="px-6 py-8 text-center text-sm text-[#64748b]">
                {filter === "all" ? "No notifications yet" : "No notifications match this filter"}
              </div>
            ) : (
              <div>
                {filteredNotifications.map((notification) => {
                  const unread = !notification.is_read;
                  return (
                    <button
                      key={notification.id}
                      type="button"
                      onClick={() => handleNotificationClick(notification)}
                      className={cn(
                        "flex w-full gap-3 border-b border-[#f4f7f9] px-5 py-4 text-left last:border-b-0",
                        unread ? "bg-[#f7fbfa]" : "bg-white hover:bg-[#f8fafc]",
                      )}
                    >
                      <span
                        className={cn(
                          "mt-[7px] h-2 w-2 shrink-0 rounded-full",
                          unread ? "bg-[#24a89a]" : "bg-transparent",
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <p
                          className={cn(
                            "text-[15px] tracking-[-0.1px]",
                            unread ? "font-bold text-[#0f1720]" : "font-medium text-[#3f4c58]",
                          )}
                        >
                          {notification.title}
                        </p>
                        <p
                          className={cn(
                            "mt-0.5 whitespace-pre-line text-sm leading-snug",
                            unread ? "font-medium text-[#3f4c58]" : "font-normal text-[#64748b]",
                          )}
                        >
                          {notification.message}
                        </p>
                        <p className="mt-2 text-xs font-medium text-[#94a3b8]">
                          {format(new Date(notification.created_at), "MMM d, yyyy 'at' h:mm a")} ·{" "}
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </PageContainer>
    </AppLayout>
  );
}
